<template>
  <div class="px-4 py-5">
    <LoadingState :loading="loadingJob" :message="jobError" />

    <div v-if="job && !loadingJob" class="space-y-4">
      <div class="card-panel p-4 text-left">
        <h1 class="text-base font-extrabold text-slate-800">{{ entryTitle }}</h1>
        <div class="mt-3 text-sm text-slate-700 space-y-3">
          <div class="space-y-1">
            <p class="font-bold text-slate-800">Job Title: {{ display(selectedEntry?.title) }}</p>
            <p><span class="font-bold text-slate-800">Job Type:</span> {{ display(selectedEntry?.job_type) }}</p>
            <p><span class="font-bold text-slate-800">Education Level:</span> {{ display(selectedEntry?.education_level) }}</p>
            <p><span class="font-bold text-slate-800">Experience:</span> {{ display(selectedEntry?.experience) }}</p>
            <p><span class="font-bold text-slate-800">Sex:</span> {{ display(selectedEntry?.sex) }}</p>
            <p><span class="font-bold text-slate-800">No Of Employees:</span> {{ display(selectedEntry?.no_of_employees) }}</p>
            <!-- <p><span class="font-bold text-slate-800">Sector:</span> {{ display(selectedEntry?.sector) }}</p> -->
            <p><span class="font-bold text-slate-800">Salary:</span> {{ display(selectedEntry?.salary) }}</p>
          </div>
          <div class="border-t border-slate-100 pt-3 space-y-1.5">
            <p v-for="row in commonInfoRows" :key="row.label">
              <span class="font-bold text-slate-800">{{ row.label }}:</span>
              {{ row.value }}
            </p>
          </div>
        </div>
        <div v-if="job.description" class="mt-3">
          <p class="field-label mb-1">Description</p>
          <p class="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">{{ job.description }}</p>
        </div>
      </div>

      <div class="card-panel p-5">
        <h2 class="text-lg font-extrabold text-slate-800 text-left mb-4">Application Form</h2>
        <form class="space-y-4" @submit.prevent="submit">
          <div class="flex flex-col gap-1">
            <label class="field-label">Full Name</label>
            <input v-model.trim="form.name" class="input-field" required />
          </div>
          <div class="flex flex-col gap-1">
            <label class="field-label">Phone Number</label>
            <input v-model.trim="form.phone" class="input-field" required />
          </div>
          <div class="flex flex-col gap-1">
            <label class="field-label">Telegram Username</label>
            <input v-model.trim="form.telegram_username" class="input-field bg-slate-50" readonly />
          </div>
          <div class="flex flex-col gap-1">
            <label class="field-label">Short Message (Optional)</label>
            <textarea v-model.trim="form.message" class="input-field min-h-[90px]" placeholder="Introduce yourself briefly" />
          </div>
          <div class="flex flex-col gap-1">
            <label class="field-label">CV Upload (Optional)</label>
            <input
              type="file"
              multiple
              accept=".pdf,.jpg,.jpeg,.png,.webp,.gif,application/pdf,image/*"
              class="text-xs"
              @change="onFile"
            />
            <p class="text-[11px] text-slate-500 text-left">
              You can upload multiple PDF or image files.
            </p>
            <ul v-if="cvFiles.length" class="text-[11px] text-slate-600 text-left space-y-0.5">
              <li v-for="(file, index) in cvFiles" :key="`${file.name}-${index}`">
                {{ index + 1 }}. {{ file.name }}
              </li>
            </ul>
          </div>

          <p v-if="error" class="text-rose-600 text-xs text-center">{{ error }}</p>
          <button type="submit" class="btn-primary" :disabled="loading">
            {{ loading ? 'Submitting…' : 'Submit Application' }}
          </button>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, onMounted, reactive, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'
import { apiFetch } from '../utils/api'
import { getJobEntryTitle, getJobEntries } from '../utils/jobFields'
import LoadingState from '../components/LoadingState.vue'
import { hapticLight } from '../utils/telegram'

export default {
  name: 'ApplyJobView',
  components: { LoadingState },
  setup() {
    const route = useRoute()
    const router = useRouter()
    const store = useStore()
    const user = store.getters.currentUser
    const job = ref(null)
    const loadingJob = ref(true)
    const jobError = ref('')
    const loading = ref(false)
    const error = ref('')
    const cvFiles = ref([])

    const entryIndex = computed(() => Math.max(0, Number(route.query.entry) || 0))

    const form = reactive({
      name: user?.full_name || '',
      phone: '',
      telegram_username: user?.username || '',
      message: '',
    })

    function display(value) {
      const v = value == null ? '' : String(value).trim()
      return v || '—'
    }

    const jobEntries = computed(() => getJobEntries(job.value))
    const selectedEntry = computed(() => jobEntries.value[entryIndex.value] || jobEntries.value[0])
    const entryTitle = computed(() => getJobEntryTitle(job.value, entryIndex.value))

    const commonInfoRows = computed(() => {
      const j = job.value
      if (!j) return []
      const rows = [
        { label: 'Company Name', value: display(j.company_name) },
        { label: 'Work Address', value: display(j.work_address) },
        { label: 'Work Location', value: display(j.work_location) },
        { label: 'Job Deadline', value: j.job_deadline ? String(j.job_deadline).slice(0, 10) : '—' },
      ]
      const jobPhone = j.phone == null ? '' : String(j.phone).trim()
      if (jobPhone) rows.push({ label: 'Phone', value: jobPhone })
      return rows
    })

    function onFile(e) {
      const files = Array.from(e.target.files || [])
      const allowed = files.filter((file) => {
        const type = String(file.type || '').toLowerCase()
        const name = String(file.name || '').toLowerCase()
        return (
          type === 'application/pdf' ||
          type.startsWith('image/') ||
          name.endsWith('.pdf') ||
          /\.(png|jpe?g|gif|webp)$/.test(name)
        )
      })
      if (files.length && allowed.length !== files.length) {
        error.value = 'Only PDF and image files are allowed'
      } else {
        error.value = ''
      }
      cvFiles.value = allowed.slice(0, 10)
    }

    async function loadJob() {
      loadingJob.value = true
      try {
        const res = await apiFetch(`/jobs/${route.params.id}`)
        const data = await res.json().catch(() => ({}))
        if (!res.ok) throw new Error(data.message || 'Job not found')
        job.value = data.job
        if (entryIndex.value >= getJobEntries(data.job).length) {
          jobError.value = 'Invalid job role selection'
          job.value = null
        }
      } catch (err) {
        jobError.value = err.message
      } finally {
        loadingJob.value = false
      }
    }

    async function submit() {
      error.value = ''
      loading.value = true
      try {
        const body = new FormData()
        body.append('job_id', String(route.params.id))
        body.append('job_entry_index', String(entryIndex.value))
        body.append('name', form.name)
        body.append('phone', form.phone)
        body.append('telegram_username', form.telegram_username || '')
        body.append('message', form.message || '')
        cvFiles.value.forEach((file) => body.append('cv', file))

        const res = await apiFetch('/applications', { method: 'POST', body })
        const data = await res.json().catch(() => ({}))
        if (!res.ok) throw new Error(data.message || 'Failed to apply')
        hapticLight()
        router.push({ name: 'my_applications' })
      } catch (err) {
        error.value = err.message
      } finally {
        loading.value = false
      }
    }

    onMounted(loadJob)
    return {
      job,
      loadingJob,
      jobError,
      selectedEntry,
      entryTitle,
      commonInfoRows,
      display,
      form,
      loading,
      error,
      cvFiles,
      onFile,
      submit,
    }
  },
}
</script>
